import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, FlyControls, MapControls, OrthographicCamera } from "@react-three/drei";
import { Play, Pause, FastForward, StepForward, Video, Plane, Move3d, Radio, Zap, GitBranch, Crosshair } from "lucide-react";
import { useSdkStore } from "./sdkStore";
import { buildSimulation, posAt, headingAt } from "./simulation";

const KIND_STYLE = {
  player: { color: "#22c55e", cone: "#22c55e", label: "YOU" },
  police: { color: "#2E6BFF", cone: "#2E6BFF", label: "UNIT" },
  swat: { color: "#67e8f9", cone: "#67e8f9", label: "SWAT" },
  suspect: { color: "#ef4444", cone: "#ef4444", label: "SUSPECT" },
  civilian: { color: "#a1a1aa", cone: "#a1a1aa", label: "CIV" },
  hostage: { color: "#FF6B1A", cone: "#FF6B1A", label: "HOSTAGE" },
  vehicle: { color: "#52525b", cone: null, label: "VEH" },
};

function Ticker({ timeRef }) {
  const playing = useSdkStore((s) => s.sim.playing);
  const speed = useSdkStore((s) => s.sim.speed);
  useFrame((_, dt) => {
    if (playing) {
      timeRef.current = Math.min(timeRef.current + dt * speed, 150);
      if (timeRef.current >= 150) useSdkStore.getState().setSim({ playing: false });
    }
  });
  return null;
}

function Entity({ e, timeRef }) {
  const ref = useRef(null);
  const style = KIND_STYLE[e.kind] || KIND_STYLE.civilian;
  const isVeh = e.kind === "vehicle";
  useFrame(() => {
    if (!ref.current) return;
    const t = timeRef.current;
    const [x, z] = posAt(e.track, t);
    ref.current.position.set(x, 0, z);
    ref.current.rotation.y = headingAt(e.track, t);
    ref.current.visible = !e.spawnAt || t >= e.spawnAt;
  });
  return (
    <group ref={ref}>
      {isVeh ? (
        <mesh position={[0, 1, 0]}><boxGeometry args={[2.4, 1.4, 5]} /><meshStandardMaterial color={style.color} /></mesh>
      ) : (
        <>
          <mesh position={[0, 1.1, 0]}><boxGeometry args={[1.1, 2.2, 0.7]} /><meshStandardMaterial color={style.color} /></mesh>
          <mesh position={[0, 2.6, 0]}><boxGeometry args={[0.6, 0.6, 0.6]} /><meshStandardMaterial color={style.color} /></mesh>
          {e.fov > 0 && (
            <mesh position={[0, 0.35, e.fov / 2.2]} rotation={[-Math.PI / 2, 0, 0]}>
              <coneGeometry args={[e.fov / 3.2, e.fov / 1.1, 20, 1, true]} />
              <meshBasicMaterial color={style.cone} transparent opacity={0.13} side={2} depthWrite={false} />
            </mesh>
          )}
        </>
      )}
    </group>
  );
}

function Zone({ z, timeRef }) {
  return (
    <group position={[z.x, 0.06, z.z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[z.r - 0.7, z.r, 48]} />
        <meshBasicMaterial color={z.color} transparent opacity={0.8} depthWrite={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[z.r, 48]} />
        <meshBasicMaterial color={z.color} transparent opacity={0.05} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Barrier({ b, timeRef }) {
  const ref = useRef(null);
  useFrame(() => { if (ref.current) ref.current.visible = timeRef.current >= b.at; });
  return (
    <group ref={ref} position={[b.x, 0, b.z]}>
      <mesh position={[0, 0.8, 0]}><boxGeometry args={[6, 1.2, 0.4]} /><meshStandardMaterial color="#FF6B1A" /></mesh>
      <mesh position={[-2.4, 0.35, 0]}><boxGeometry args={[0.4, 0.9, 0.4]} /><meshStandardMaterial color="#71717a" /></mesh>
      <mesh position={[2.4, 0.35, 0]}><boxGeometry args={[0.4, 0.9, 0.4]} /><meshStandardMaterial color="#71717a" /></mesh>
    </group>
  );
}

function Scene({ sim, camMode, timeRef }) {
  return (
    <>
      <color attach="background" args={["#050505"]} />
      <fog attach="fog" args={["#050505", 140, 320]} />
      <ambientLight intensity={0.5} />
      <directionalLight position={[60, 90, 40]} intensity={1.1} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]}>
        <planeGeometry args={[400, 400]} />
        <meshStandardMaterial color="#0a0a0c" />
      </mesh>
      <gridHelper args={[400, 80, "#1c1c22", "#121216"]} position={[0, 0.01, 0]} />
      {sim.buildings.map((b, i) => (
        <group key={i} position={[b.x, 0, b.z]}>
          <mesh position={[0, b.h / 2, 0]}>
            <boxGeometry args={[b.w, b.h, b.d]} />
            <meshStandardMaterial color="#18181d" />
          </mesh>
          <mesh position={[0, b.h / 2, 0]}>
            <boxGeometry args={[b.w + 0.15, b.h + 0.15, b.d + 0.15]} />
            <meshBasicMaterial color="#2E6BFF" wireframe transparent opacity={0.12} />
          </mesh>
        </group>
      ))}
      {sim.zones.map((z) => <Zone key={z.id} z={z} timeRef={timeRef} />)}
      {(sim.barriers || []).map((b) => <Barrier key={b.id} b={b} timeRef={timeRef} />)}
      {sim.entities.map((e) => <Entity key={e.id} e={e} timeRef={timeRef} />)}
      <Ticker timeRef={timeRef} />
      {camMode === "orbit" && <OrbitControls makeDefault maxPolarAngle={Math.PI / 2.15} minDistance={20} maxDistance={260} target={[0, 0, 20]} />}
      {camMode === "fly" && <FlyControls makeDefault movementSpeed={40} rollSpeed={0.4} dragToLook />}
      {camMode === "top" && (
        <>
          <OrthographicCamera makeDefault position={[0, 200, 0.01]} zoom={3.2} near={1} far={500} />
          <MapControls makeDefault enableRotate={false} />
        </>
      )}
    </>
  );
}

const EVENT_ICON = { trigger: Zap, logic: GitBranch, action: Crosshair, radio: Radio };

export default function SandboxTab() {
  const nodes = useSdkStore((s) => s.nodes);
  const edges = useSdkStore((s) => s.edges);
  const mission = useSdkStore((s) => s.mission);
  const simState = useSdkStore((s) => s.sim);
  const setSim = useSdkStore((s) => s.setSim);
  const [scenarioId, setScenarioId] = useState(mission.scenario === "barricade" ? "barricade" : "active_shooter");
  const [camMode, setCamMode] = useState("orbit");
  const [displayT, setDisplayT] = useState(0);
  const timeRef = useRef(0);

  const sim = useMemo(() => buildSimulation(scenarioId, nodes, edges), [scenarioId, nodes, edges]);

  useEffect(() => {
    const iv = setInterval(() => setDisplayT(timeRef.current), 200);
    return () => clearInterval(iv);
  }, []);

  const seek = (t) => { timeRef.current = Math.max(0, Math.min(150, t)); setDisplayT(timeRef.current); };
  const step = () => { setSim({ playing: false }); seek(timeRef.current + 1); };

  return (
    <div data-testid="sandbox-tab" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-[#070707] p-4 flex flex-wrap items-center gap-3">
        <div>
          <span className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase block mb-1">Scenario</span>
          <select data-testid="sandbox-scenario" value={scenarioId} onChange={(e) => { setScenarioId(e.target.value); seek(0); }} className="bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[11px] text-zinc-100 focus:outline-none">
            <option value="active_shooter">Active Shooter — Northgate Mall</option>
            <option value="barricade">High-Risk Barricade — Reseda Ave</option>
          </select>
        </div>
        <div className="flex gap-1.5 ml-auto">
          {[["orbit", Move3d, "Orbit"], ["fly", Video, "Fly (WASD)"], ["top", Plane, "Top-Down"]].map(([m, Icon, label]) => (
            <button key={m} data-testid={`sandbox-cam-${m}`} onClick={() => setCamMode(m)} className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${camMode === m ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/15 text-zinc-400 hover:text-zinc-100"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <div data-testid="sandbox-viewport" className="border border-white/10 bg-black h-[600px] relative">
          <Canvas camera={{ position: [70, 70, 110], fov: 50 }}>
            <Scene sim={sim} camMode={camMode} timeRef={timeRef} />
          </Canvas>
          <div className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 bg-black/60 border border-white/10 px-2.5 py-1.5">
            {sim.label} // T+{displayT.toFixed(0)}s
          </div>
          <div className="absolute top-3 right-3 flex gap-2 font-mono text-[8px] uppercase">
            {Object.entries(KIND_STYLE).filter(([k]) => k !== "vehicle").map(([k, v]) => (
              <span key={k} className="flex items-center gap-1 bg-black/60 border border-white/10 px-2 py-1" style={{ color: v.color }}>
                <span className="w-1.5 h-1.5 inline-block" style={{ background: v.color }} /> {v.label}
              </span>
            ))}
          </div>
        </div>

        {/* execution log */}
        <div className="border border-white/10 bg-[#070707] flex flex-col h-[600px]">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase px-4 py-3 border-b border-white/10">Node Execution // Live Trace</p>
          <div data-testid="sandbox-event-log" className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
            {sim.events.length === 0 && <p className="font-mono text-[10px] text-zinc-600 p-3 leading-relaxed">No blueprint connected. Build a trigger → action chain in Mission Blueprint and it executes here against the scenario.</p>}
            {sim.events.map((ev, i) => {
              const passed = displayT >= ev.t;
              const Icon = EVENT_ICON[ev.kind] || Zap;
              return (
                <button key={i} data-testid={i < 12 ? `sandbox-event-${i}` : undefined} onClick={() => seek(ev.t)} className={`text-left px-3 py-2 border font-mono text-[9px] transition-colors flex items-center gap-2 ${passed ? "border-nc-green/30 text-zinc-200 bg-nc-green/5" : "border-white/5 text-zinc-600 hover:text-zinc-400"}`}>
                  <Icon className={`w-3 h-3 shrink-0 ${passed ? "text-nc-green" : "text-zinc-700"}`} />
                  <span className="flex-1 truncate">{ev.label}</span>
                  <span className="text-zinc-600 shrink-0">T+{ev.t}s</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* timeline */}
      <div className="border border-white/10 bg-[#070707] p-4 flex items-center gap-3 flex-wrap">
        <button data-testid="sandbox-play" onClick={() => setSim({ playing: !simState.playing })} className="bg-nc-blue hover:bg-nc-blue/80 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 flex items-center gap-2 transition-colors">
          {simState.playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {simState.playing ? "Pause" : "Play"}
        </button>
        <button data-testid="sandbox-step" onClick={step} className="border border-white/15 text-zinc-300 hover:text-white font-mono text-[10px] uppercase px-3 py-2.5 flex items-center gap-1.5 transition-colors"><StepForward className="w-3.5 h-3.5" /> +1s</button>
        {[1, 2, 4].map((s) => (
          <button key={s} data-testid={`sandbox-speed-${s}`} onClick={() => setSim({ speed: s })} className={`font-mono text-[9px] px-2.5 py-2 border transition-colors flex items-center gap-1 ${simState.speed === s ? "border-nc-orange text-nc-orange" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>
            <FastForward className="w-3 h-3" /> {s}x
          </button>
        ))}
        <input data-testid="sandbox-scrub" type="range" min={0} max={150} step={0.5} value={displayT} onChange={(e) => seek(parseFloat(e.target.value))} className="flex-1 min-w-40 accent-[#2E6BFF]" />
        <span data-testid="sandbox-time" className="font-mono text-[11px] text-nc-blue w-20 text-right">T+{displayT.toFixed(1)}s</span>
      </div>

      <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">
        The sandbox executes your Mission Blueprint against the scenario in real time — triggers fire on the timeline, logic branches resolve, and actions spawn units, barricades and radio traffic in the viewport. Directional cones show AI field-of-view.
      </p>
    </div>
  );
}
